// ============================================================================
// Entity: SkillGroups
// Key: skillGroups
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getSkillGroupsListQueryKey,
	getSkillGroupsListQueryOptions,
	getSkillGroupsRelationsSkillsAssignedQueryKey,
	getSkillGroupsRelationsSkillsAssignedQueryOptions,
	getSkillGroupsRelationsSkillsAvailableQueryKey,
	getSkillGroupsRelationsSkillsAvailableQueryOptions,
	getSkillGroupsRelationsTeamsAssignedQueryKey,
	getSkillGroupsRelationsTeamsAssignedQueryOptions,
	getSkillGroupsRelationsTeamsAvailableQueryKey,
	getSkillGroupsRelationsTeamsAvailableQueryOptions,
	getSkillGroupsRelationsUsersAssignedQueryKey,
	getSkillGroupsRelationsUsersAssignedQueryOptions,
	getSkillGroupsRelationsUsersAvailableQueryKey,
	getSkillGroupsRelationsUsersAvailableQueryOptions,
	getSkillsListQueryKey,
	getTeamsListQueryKey,
	getUsersListQueryKey,
	skillGroupsCreate,
	skillGroupsDelete,
	skillGroupsRelationsSkillsAssign,
	skillGroupsRelationsSkillsUnassign,
	skillGroupsRelationsTeamsAssign,
	skillGroupsRelationsTeamsUnassign,
	skillGroupsRelationsUsersAssign,
	skillGroupsRelationsUsersUnassign,
	skillGroupsRelationsUsersUpdate,
	skillGroupsUpdate,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdateSkillGroupRequest,
	PagedResultOfSkillGroupSkillDto,
	PagedResultOfSkillGroupSummary,
	PagedResultOfSkillSummary,
	PagedResultOfTeamSkillGroupResponse,
	PagedResultOfTeamSummary,
	PagedResultOfUserDto,
	PagedResultOfUserSkillGroupDto,
	SkillGroupSkillDto,
	SkillGroupSummary,
	TeamSkillGroupResponse,
	UpdateUserSkillGroupRequest,
	UserSkillGroupDto,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const skillGroupsEntityEndpoints = {
	create: defineMutationEndpoint<
		SkillGroupSummary,
		CreateOrUpdateSkillGroupRequest,
		undefined
	>({
		key: 'skillGroups.create',
		method: 'POST',
		path: '/admin/skillgroups',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateSkillGroupRequest }) =>
				skillGroupsCreate(variables.data),
		}),
		invalidates: [(_context) => getSkillGroupsListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { skillGroupId: number }>({
		key: 'skillGroups.delete',
		method: 'DELETE',
		path: '/admin/skillgroups/{skillGroupId}',
		pathParams: ['skillGroupId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				skillGroupId: number | string | null | undefined
			}) => skillGroupsDelete(variables.skillGroupId),
		}),
		invalidates: [(_context) => getSkillGroupsListQueryKey()],
	}),
	list: defineQueryEndpoint<
		PagedResultOfSkillGroupSummary,
		undefined,
		ListQueryParams
	>({
		key: 'skillGroups.list',
		method: 'GET',
		path: '/admin/skillgroups',
		queryKey: (args) => getSkillGroupsListQueryKey(args?.queryParams),
		queryOptions: (args) => getSkillGroupsListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		SkillGroupSummary,
		CreateOrUpdateSkillGroupRequest,
		{ skillGroupId: number }
	>({
		key: 'skillGroups.update',
		method: 'PUT',
		path: '/admin/skillgroups/{skillGroupId}',
		pathParams: ['skillGroupId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				skillGroupId: number | string | null | undefined
				data: CreateOrUpdateSkillGroupRequest
			}) => skillGroupsUpdate(variables.skillGroupId, variables.data),
		}),
		invalidates: [(_context) => getSkillGroupsListQueryKey()],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: Skills
// Target Entity: skills
const skillGroupsSkillsRelation = {
	key: 'skills',
	targetEntity: 'skills',
	endpoints: {
		assign: defineMutationEndpoint<
			SkillGroupSkillDto[],
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.skills.assign',
			method: 'POST',
			path: '/admin/skillgroups/{skillGroupId}/skills',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsSkillsAssign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsSkillsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsSkillsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getSkillsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfSkillGroupSkillDto,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.skills.assigned',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/skills',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsSkillsAssignedQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsSkillsAssignedQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfSkillSummary,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.skills.available',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/assignable-skills',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsSkillsAvailableQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsSkillsAvailableQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.skills.unassign',
			method: 'DELETE',
			path: '/admin/skillgroups/{skillGroupId}/skills',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsSkillsUnassign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsSkillsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsSkillsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getSkillsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Teams
// Target Entity: teams
const skillGroupsTeamsRelation = {
	key: 'teams',
	targetEntity: 'teams',
	endpoints: {
		assign: defineMutationEndpoint<
			TeamSkillGroupResponse[],
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.teams.assign',
			method: 'POST',
			path: '/admin/skillgroups/{skillGroupId}/teams',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsTeamsAssign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfTeamSkillGroupResponse,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.teams.assigned',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/teams',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsTeamsAssignedQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsTeamsAssignedQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfTeamSummary,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.teams.available',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/assignable-teams',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsTeamsAvailableQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsTeamsAvailableQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.teams.unassign',
			method: 'DELETE',
			path: '/admin/skillgroups/{skillGroupId}/teams',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsTeamsUnassign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const skillGroupsUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			UserSkillGroupDto[],
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.users.assign',
			method: 'POST',
			path: '/admin/skillgroups/{skillGroupId}/users',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsUsersAssign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfUserSkillGroupDto,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.users.assigned',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/users',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsUsersAssignedQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsUsersAssignedQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfUserDto,
			{ skillGroupId: number },
			ListQueryParams
		>({
			key: 'skillGroups.relations.users.available',
			method: 'GET',
			path: '/admin/skillgroups/{skillGroupId}/assignable-users',
			pathParams: ['skillGroupId'] as const,
			queryKey: (args) =>
				getSkillGroupsRelationsUsersAvailableQueryKey(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillGroupsRelationsUsersAvailableQueryOptions(
					args?.pathParams?.skillGroupId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillGroupId: number }
		>({
			key: 'skillGroups.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/skillgroups/{skillGroupId}/users',
			pathParams: ['skillGroupId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillGroupsRelationsUsersUnassign(
						variables.skillGroupId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		update: defineMutationEndpoint<
			UserSkillGroupDto,
			UpdateUserSkillGroupRequest,
			{ skillGroupId: number; mappingId: number }
		>({
			key: 'skillGroups.relations.users.update',
			method: 'PUT',
			path: '/admin/skillgroups/{skillGroupId}/users/{mappingId}',
			pathParams: ['skillGroupId', 'mappingId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillGroupId: number | string | null | undefined
					mappingId: number | string | null | undefined
					data: UpdateUserSkillGroupRequest
				}) =>
					skillGroupsRelationsUsersUpdate(
						variables.skillGroupId,
						variables.mappingId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getSkillGroupsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillGroupsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillGroupsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Entity Config Export
export const skillGroupsEntityConfig = {
	key: 'skillGroups',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'skillGroupId',
		routePath: '/skillGroups',
	},
	endpoints: skillGroupsEntityEndpoints,
	relations: {
		skills: skillGroupsSkillsRelation,
		teams: skillGroupsTeamsRelation,
		users: skillGroupsUsersRelation,
	},
} as const satisfies EntityConfig<'skillGroups'>
// #endregion
